import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { mediaApi } from '../services/api';

const UploadContext = createContext();

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload must be used within an UploadProvider');
    }
    return context;
};

export const UploadProvider = ({ children }) => {
    const [uploads, setUploads] = useState({});

    const updateUpload = useCallback((id, updates) => {
        setUploads(prev => ({
            ...prev,
            [id]: { ...prev[id], ...updates }
        }));
    }, []);

    const uploadFile = async (file, onComplete) => {
        const uploadId = Math.random().toString(36).substring(7);
        const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB per chunk

        setUploads(prev => ({
            ...prev,
            [uploadId]: {
                id: uploadId,
                filename: file.name,
                progress: 0,
                status: 'uploading',
                error: null
            }
        }));

        try {
            // 1. Initiate Multipart Upload
            const { uploadId: r2UploadId, mediaId, r2Path } = await mediaApi.initiateUpload(file.name, file.type);
            updateUpload(uploadId, { mediaId });

            const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
            const uploadedParts = [];

            // 2. Upload Chunks
            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file.size);
                const chunk = file.slice(start, end);
                const partNumber = i + 1;

                // Get presigned URL for this part
                const { url } = await mediaApi.presignPart(r2Path, r2UploadId, partNumber);

                // Push chunk to R2 (Directly to R2, so no /api/ or auth needed here as it's a presigned URL)
                const uploadPartRes = await axios.put(url, chunk, {
                    headers: { 'Content-Type': file.type },
                    onUploadProgress: (progressEvent) => {
                        const chunkProgress = (progressEvent.loaded / progressEvent.total) * (1 / totalChunks) * 100;
                        const overallProgress = (i / totalChunks) * 100 + chunkProgress;
                        updateUpload(uploadId, { progress: Math.min(Math.round(overallProgress), 99) });
                    }
                });

                const etag = uploadPartRes.headers.etag;
                uploadedParts.push({ ETag: etag, PartNumber: partNumber });
            }

            // 3. Complete Multipart Upload
            updateUpload(uploadId, { status: 'verifying' });
            const completeRes = await mediaApi.completeUpload(r2UploadId, r2Path, mediaId, uploadedParts);

            updateUpload(uploadId, {
                status: 'success',
                progress: 100,
                publicUrl: completeRes.url
            });

            if (onComplete) onComplete(completeRes.url, mediaId);

        } catch (error) {
            console.error('Upload failed:', error);
            updateUpload(uploadId, {
                status: 'failed',
                error: error.response?.data?.error || error.message || 'Upload failed'
            });
            if (onComplete) onComplete(null, null);
        }
    };

    const removeUpload = (id) => {
        setUploads(prev => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    return (
        <UploadContext.Provider value={{ uploads, uploadFile, removeUpload }}>
            {children}
        </UploadContext.Provider>
    );
};
